/**
 * Dashboard Management Slice
 *
 * Following the same architecture pattern as authSlice.ts
 * SOLID Principles Applied:
 * - Single Responsibility: Only manages dashboard state (reducers)
 * - Separation of Concerns:
 *   • State management (this file)
 *   • Async operations (dashboardThunks.ts)
 *   • Data access (dashboardSelectors.ts)
 * - Open/Closed: Easy to add new reducers without modifying existing ones
 * - Dependency Inversion: Depends on thunks abstraction, not implementation
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IProductManagementState,
  IOrderManagementState,
  IProductSearchParams,
  IOrderSearchParams,
  IProduct,
  IOrder,
  IProductAnalytics,
  IOrderAnalytics,
  IPrintQueueSummary,
} from "../../../types/dashboard.types";

// Import async thunks (Separation of Concerns)
import {
  // Product thunks
  fetchProducts,
  fetchProductAnalytics,
  createProduct,
  updateProduct,
  deleteProduct,
  bulkDeleteProducts,
  updateProductStock,
  bulkUpdateStock,
  exportProductsToCsv,
  createProductVariant,
  updateProductVariant,
  deleteProductVariant,
  // Order thunks
  fetchOrders,
  fetchOrderAnalytics,
  fetchPrintQueueSummary,
  updateOrderStatus,
  updatePaymentStatus,
  addTrackingNumber,
  processRefund,
  exportOrdersToCsv,
  printShippingLabel,
} from "./dashboardThunks";

// ============================================
// INITIAL STATES
// ============================================

const initialProductState: IProductManagementState = {
  products: [],
  analytics: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
  loading: false,
  error: null,
  searchParams: {},
  operations: {
    creating: false,
    updating: false,
    deleting: false,
    bulkDeleting: false,
    updatingStock: false,
    exporting: false,
  },
};

const initialOrderState: IOrderManagementState = {
  orders: [],
  analytics: null,
  printQueueSummary: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
  loading: false,
  error: null,
  searchParams: {},
  operations: {
    updatingStatus: false,
    updatingPayment: false,
    addingTracking: false,
    processingRefund: false,
    exporting: false,
    printingLabel: false,
  },
};

// ============================================
// PRODUCT MANAGEMENT SLICE
// ============================================

const productManagementSlice = createSlice({
  name: "productManagement",
  initialState: initialProductState,
  reducers: {
    // Synchronous actions
    setProductSearchParams: (
      state,
      action: PayloadAction<IProductSearchParams>,
    ) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    clearProductError: (state) => {
      state.error = null;
    },
    clearProducts: (state) => {
      state.products = [];
      state.pagination = initialProductState.pagination;
    },
    resetProductState: () => initialProductState,

    // Optimistic updates
    updateProductOptimistically: (state, action: PayloadAction<IProduct>) => {
      const index = state.products.findIndex(
        (product) => product.id === action.payload.id,
      );
      if (index !== -1) {
        state.products[index] = action.payload;
      }
    },
    removeProductOptimistically: (state, action: PayloadAction<string>) => {
      state.products = state.products.filter(
        (product) => product.id !== action.payload,
      );
      state.pagination.total = Math.max(0, state.pagination.total - 1);
    },
  },
  extraReducers: (builder) => {
    // Fetch Products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Product Analytics
    builder
      .addCase(fetchProductAnalytics.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchProductAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create Product
    builder
      .addCase(createProduct.pending, (state) => {
        state.operations.creating = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.operations.creating = false;
        state.products.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.operations.creating = false;
        state.error = action.payload as string;
      });

    // Update Product
    builder
      .addCase(updateProduct.pending, (state) => {
        state.operations.updating = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.operations.updating = false;
        const index = state.products.findIndex(
          (product) => product.id === action.payload.id,
        );
        if (index !== -1) {
          state.products[index] = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.operations.updating = false;
        state.error = action.payload as string;
      });

    // Delete Product
    builder
      .addCase(deleteProduct.pending, (state) => {
        state.operations.deleting = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.operations.deleting = false;
        const { productId } = action.payload;
        const index = state.products.findIndex((p) => p.id === productId);
        if (index >= 0) {
          state.products[index] = {
            ...state.products[index],
            status: "ARCHIVED",
          };
        }
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.operations.deleting = false;
        state.error = action.payload as string;
      });

    // Bulk Delete Products
    builder
      .addCase(bulkDeleteProducts.pending, (state) => {
        state.operations.bulkDeleting = true;
        state.error = null;
      })
      .addCase(bulkDeleteProducts.fulfilled, (state, action) => {
        state.operations.bulkDeleting = false;
        const results = action.payload.results || [];
        const archivedIds = results
          .filter((r) => r.success !== false)
          .map((r) => r.productId);

        archivedIds.forEach((productId) => {
          const index = state.products.findIndex((p) => p.id === productId);
          if (index >= 0) {
            state.products[index] = {
              ...state.products[index],
              status: "ARCHIVED",
            };
          }
        });
      })
      .addCase(bulkDeleteProducts.rejected, (state, action) => {
        state.operations.bulkDeleting = false;
        state.error = action.payload as string;
      });

    // Update Product Stock
    builder
      .addCase(updateProductStock.pending, (state) => {
        state.operations.updatingStock = true;
        state.error = null;
      })
      .addCase(updateProductStock.fulfilled, (state, action) => {
        state.operations.updatingStock = false;
        const index = state.products.findIndex(
          (product) => product.id === action.payload.id,
        );
        if (index !== -1) {
          state.products[index] = {
            ...state.products[index],
            ...action.payload,
          };
        }
      })
      .addCase(updateProductStock.rejected, (state, action) => {
        state.operations.updatingStock = false;
        state.error = action.payload as string;
      });

    // Export Products
    builder
      .addCase(exportProductsToCsv.pending, (state) => {
        state.operations.exporting = true;
        state.error = null;
      })
      .addCase(exportProductsToCsv.fulfilled, (state) => {
        state.operations.exporting = false;
      })
      .addCase(exportProductsToCsv.rejected, (state, action) => {
        state.operations.exporting = false;
        state.error = action.payload as string;
      });
  },
});

// ============================================
// ORDER MANAGEMENT SLICE
// ============================================

const orderManagementSlice = createSlice({
  name: "orderManagement",
  initialState: initialOrderState,
  reducers: {
    // Synchronous actions
    setOrderSearchParams: (
      state,
      action: PayloadAction<IOrderSearchParams>,
    ) => {
      // Replace searchParams completely, don't merge (prevents stale filter values)
      state.searchParams = { ...action.payload };
    },
    clearOrderError: (state) => {
      state.error = null;
    },
    clearOrders: (state) => {
      state.orders = [];
      state.pagination = initialOrderState.pagination;
    },
    resetOrderState: () => initialOrderState,

    // Optimistic updates
    updateOrderOptimistically: (state, action: PayloadAction<IOrder>) => {
      const index = state.orders.findIndex(
        (order) => order.id === action.payload.id,
      );
      if (index !== -1) {
        state.orders[index] = action.payload;
      }
    },
    removeOrderOptimistically: (state, action: PayloadAction<string>) => {
      state.orders = state.orders.filter(
        (order) => order.id !== action.payload,
      );
      state.pagination.total = Math.max(0, state.pagination.total - 1);
    },
  },
  extraReducers: (builder) => {
    // Fetch Orders - primary loading indicator for orders data
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Keep existing orders visible during loading to prevent flickering
        // Only clear if this is the initial load (orders.length === 0)
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        // Only set loading to false if this is the primary data fetch
        // Don't set to false here if analytics/printQueue are still loading
        // The loading state should reflect the main orders request
        state.loading = false;
        state.orders = action.payload.orders || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Order Analytics - optional, don't affect main loading state
    // Analytics loading is separate and doesn't block the UI
    builder
      .addCase(fetchOrderAnalytics.pending, (state) => {
        // Don't set main loading = true for analytics (it's optional/background)
      })
      .addCase(fetchOrderAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
      })
      .addCase(fetchOrderAnalytics.rejected, (state, action) => {
        // Analytics failures don't affect main loading state
      });

    // Fetch Print Queue Summary - optional, don't affect main loading state
    // Print queue loading is separate and doesn't block the UI
    builder
      .addCase(fetchPrintQueueSummary.pending, (state) => {
        // Don't set main loading = true for print queue (it's optional/background)
      })
      .addCase(fetchPrintQueueSummary.fulfilled, (state, action) => {
        state.printQueueSummary = action.payload;
      })
      .addCase(fetchPrintQueueSummary.rejected, (state, action) => {
        // Print queue failures don't affect main loading state
      });

    // Update Order Status
    builder
      .addCase(updateOrderStatus.pending, (state) => {
        state.operations.updatingStatus = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.operations.updatingStatus = false;
        const index = state.orders.findIndex(
          (order) => order.id === action.payload.id,
        );
        if (index !== -1) {
          state.orders[index] = { ...state.orders[index], ...action.payload };
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.operations.updatingStatus = false;
        state.error = action.payload as string;
      });

    // Update Payment Status
    builder
      .addCase(updatePaymentStatus.pending, (state) => {
        state.operations.updatingPayment = true;
        state.error = null;
      })
      .addCase(updatePaymentStatus.fulfilled, (state, action) => {
        state.operations.updatingPayment = false;
        const index = state.orders.findIndex(
          (order) => order.id === action.payload.id,
        );
        if (index !== -1) {
          state.orders[index] = { ...state.orders[index], ...action.payload };
        }
      })
      .addCase(updatePaymentStatus.rejected, (state, action) => {
        state.operations.updatingPayment = false;
        state.error = action.payload as string;
      });

    // Add Tracking Number
    builder
      .addCase(addTrackingNumber.pending, (state) => {
        state.operations.addingTracking = true;
        state.error = null;
      })
      .addCase(addTrackingNumber.fulfilled, (state, action) => {
        state.operations.addingTracking = false;
        const index = state.orders.findIndex(
          (order) => order.id === action.payload.id,
        );
        if (index !== -1) {
          state.orders[index] = { ...state.orders[index], ...action.payload };
        }
      })
      .addCase(addTrackingNumber.rejected, (state, action) => {
        state.operations.addingTracking = false;
        state.error = action.payload as string;
      });

    // Process Refund
    builder
      .addCase(processRefund.pending, (state) => {
        state.operations.processingRefund = true;
        state.error = null;
      })
      .addCase(processRefund.fulfilled, (state, action) => {
        state.operations.processingRefund = false;
        const index = state.orders.findIndex(
          (order) => order.id === action.payload.id,
        );
        if (index !== -1) {
          state.orders[index] = { ...state.orders[index], ...action.payload };
        }
      })
      .addCase(processRefund.rejected, (state, action) => {
        state.operations.processingRefund = false;
        state.error = action.payload as string;
      });

    // Export Orders
    builder
      .addCase(exportOrdersToCsv.pending, (state) => {
        state.operations.exporting = true;
        state.error = null;
      })
      .addCase(exportOrdersToCsv.fulfilled, (state) => {
        state.operations.exporting = false;
      })
      .addCase(exportOrdersToCsv.rejected, (state, action) => {
        state.operations.exporting = false;
        state.error = action.payload as string;
      });

    // Print Shipping Label
    builder
      .addCase(printShippingLabel.pending, (state) => {
        state.operations.printingLabel = true;
        state.error = null;
      })
      .addCase(printShippingLabel.fulfilled, (state) => {
        state.operations.printingLabel = false;
      })
      .addCase(printShippingLabel.rejected, (state, action) => {
        state.operations.printingLabel = false;
        state.error = action.payload as string;
      });
  },
});

// ============================================
// EXPORTS
// ============================================

export const {
  // Product actions
  setProductSearchParams,
  clearProductError,
  clearProducts,
  resetProductState,
  updateProductOptimistically,
  removeProductOptimistically,

  // Order actions
  setOrderSearchParams,
  clearOrderError,
  clearOrders,
  resetOrderState,
  updateOrderOptimistically,
  removeOrderOptimistically,
} = {
  ...productManagementSlice.actions,
  ...orderManagementSlice.actions,
};

export default {
  productManagement: productManagementSlice.reducer,
  orderManagement: orderManagementSlice.reducer,
};
